import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { BLUE, GREEN, ORANGE, RED } from "../../styles/mui-theme";
import { formatCOP } from "../../utils/format";
import { DailyBudget, Resume } from "../../types/types";

const COLUMN_WIDTH = "150px";

interface ResumeTableCellProps {
  cellColor?: string;
}

const ResumeTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "cellColor",
})<ResumeTableCellProps>(({ theme, cellColor }) => ({
  color: cellColor || theme.palette.text.primary,
  fontSize: theme.typography.body1.fontSize,
  width: COLUMN_WIDTH,
}));

interface Props {
  resume: Resume | undefined;
  currentBudget: DailyBudget | undefined;
  isFetching: boolean;
}

export default function DailyBudgetResume({ resume, currentBudget, isFetching }: Props) {
  const hasData = resume && currentBudget;

  const computed = React.useMemo(() => {
    if (!resume || !currentBudget) return null;
    return {
      cash_initial: currentBudget.cash_initial,
      bank_initial: currentBudget.bank_initial,
      cash_final: currentBudget.cash_initial + resume.cash_balance,
      bank_final: currentBudget.bank_initial + resume.bank_balance,
      cash_in: resume.cash_in,
      bank_in: resume.bank_in,
      cash_out: resume.cash_out,
      bank_out: resume.bank_out,
    };
  }, [resume, currentBudget]);

  return (
    <TableContainer sx={{ p: 1, maxWidth: "750px", my: 2 }} component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell style={{ width: COLUMN_WIDTH, fontWeight: "bold" }}>CUENTA</TableCell>
            <TableCell align="left" style={{ width: COLUMN_WIDTH, fontWeight: "bold" }}>
              INICIAL
            </TableCell>
            <TableCell align="left" style={{ width: COLUMN_WIDTH, fontWeight: "bold" }}>
              ENTRADAS
            </TableCell>
            <TableCell align="left" style={{ width: COLUMN_WIDTH, fontWeight: "bold" }}>
              SALIDAS
            </TableCell>
            <TableCell align="left" style={{ width: COLUMN_WIDTH, fontWeight: "bold" }}>
              FINAL
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                Cargando...
              </TableCell>
            </TableRow>
          ) : hasData && computed ? (
            <>
              <TableRow key="bank_row">
                <ResumeTableCell
                  component="th"
                  scope="row"
                  cellColor={ORANGE}
                  sx={{ fontWeight: "bold" }}
                >
                  Banco
                </ResumeTableCell>
                <ResumeTableCell cellColor={ORANGE}>
                  {formatCOP(computed.bank_initial)}
                </ResumeTableCell>
                <ResumeTableCell align="left" cellColor={GREEN}>
                  {formatCOP(computed.bank_in)}
                </ResumeTableCell>
                <ResumeTableCell align="left" cellColor={RED}>
                  {formatCOP(computed.bank_out)}
                </ResumeTableCell>
                <ResumeTableCell cellColor={ORANGE} sx={{ fontWeight: "bold" }}>
                  {formatCOP(computed.bank_final)}
                </ResumeTableCell>
              </TableRow>
              <TableRow key="cash_row">
                <ResumeTableCell
                  component="th"
                  scope="row"
                  cellColor={BLUE}
                  sx={{ fontWeight: "bold" }}
                >
                  Efectivo
                </ResumeTableCell>
                <ResumeTableCell cellColor={BLUE}>
                  {formatCOP(computed.cash_initial)}
                </ResumeTableCell>
                <ResumeTableCell align="left" cellColor={GREEN}>
                  {formatCOP(computed.cash_in)}
                </ResumeTableCell>
                <ResumeTableCell align="left" cellColor={RED}>
                  {formatCOP(computed.cash_out)}
                </ResumeTableCell>
                <ResumeTableCell cellColor={BLUE} sx={{ fontWeight: "bold" }}>
                  {formatCOP(computed.cash_final)}
                </ResumeTableCell>
              </TableRow>
              <TableRow key="total_row">
                <ResumeTableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                  Total
                </ResumeTableCell>
                <ResumeTableCell sx={{ fontWeight: "bold" }}>
                  {formatCOP(computed.cash_initial + computed.bank_initial)}
                </ResumeTableCell>
                <ResumeTableCell align="left" cellColor={GREEN} sx={{ fontWeight: "bold" }}>
                  {formatCOP(computed.cash_in + computed.bank_in)}
                </ResumeTableCell>
                <ResumeTableCell align="left" cellColor={RED} sx={{ fontWeight: "bold" }}>
                  {formatCOP(computed.cash_out + computed.bank_out)}
                </ResumeTableCell>
                <ResumeTableCell sx={{ fontWeight: "bold" }}>
                  {formatCOP(computed.cash_final + computed.bank_final)}
                </ResumeTableCell>
              </TableRow>
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No hay datos disponibles para la fecha seleccionada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
